import React, { useState } from 'react';
import TaskPreviewCard from './TaskPreviewCard';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  ClipboardDocumentListIcon,
  CalendarDaysIcon,
  ClockIcon,
  FlagIcon,
  BookmarkIcon,
  PlusIcon,
  DocumentDuplicateIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';

const PlanPreviewCard = ({
  plan,
  onSave,
  onExportJira,
  onExportAsana,
  onTaskSave,
  onTaskEdit,
  onTaskDelete
}) => {
  const [expanded, setExpanded] = useState(true);
  const [tasksExpanded, setTasksExpanded] = useState(false);
  const [milestonesExpanded, setMilestonesExpanded] = useState(false);
  const [saving, setSaving] = useState(false);

  const totalHours = plan.tasks?.reduce((sum, t) => sum + (t.estimated_hours || 0), 0) || plan.estimated_total_hours || 0;
  const taskCount = plan.tasks?.length || 0;
  const milestoneCount = plan.milestones?.length || 0;

  const handleSave = async () => {
    if (onSave) {
      setSaving(true);
      await onSave(plan);
      setSaving(false);
    }
  };

  const styles = {
    card: {
      backgroundColor: 'white',
      border: '1px solid var(--primary-200)',
      borderRadius: '0.75rem',
      overflow: 'hidden',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
      marginBottom: '1rem'
    },
    header: {
      background: 'linear-gradient(135deg, var(--primary-50) 0%, var(--primary-100) 100%)',
      padding: '1rem',
      borderBottom: '1px solid var(--primary-200)'
    },
    headerTop: {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: '1rem'
    },
    headerInfo: {
      flex: 1
    },
    title: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      fontSize: '1.125rem',
      fontWeight: '600',
      color: 'var(--gray-900)',
      margin: 0
    },
    objective: {
      fontSize: '0.875rem',
      color: 'var(--gray-600)',
      marginTop: '0.5rem',
      lineHeight: '1.5'
    },
    stats: {
      display: 'flex',
      gap: '1rem',
      marginTop: '0.75rem'
    },
    stat: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.375rem',
      fontSize: '0.8125rem',
      color: 'var(--gray-600)',
      backgroundColor: 'white',
      padding: '0.25rem 0.625rem',
      borderRadius: '9999px',
      border: '1px solid var(--gray-200)'
    },
    statIcon: {
      width: '1rem',
      height: '1rem',
      color: 'var(--primary-500)'
    },
    expandButton: {
      background: 'none',
      border: 'none',
      padding: '0.375rem',
      cursor: 'pointer',
      color: 'var(--gray-500)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '0.375rem',
      transition: 'all 0.2s'
    },
    body: {
      padding: expanded ? '1rem' : '0',
      maxHeight: expanded ? '2000px' : '0',
      overflow: 'hidden',
      transition: 'all 0.3s ease-in-out'
    },
    section: {
      marginBottom: '1rem'
    },
    sectionHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0.5rem 0.75rem',
      backgroundColor: 'var(--gray-50)',
      borderRadius: '0.5rem',
      cursor: 'pointer',
      marginBottom: '0.5rem'
    },
    sectionTitle: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      fontSize: '0.875rem',
      fontWeight: '600',
      color: 'var(--gray-700)'
    },
    sectionIcon: {
      width: '1.125rem',
      height: '1.125rem',
      color: 'var(--primary-500)'
    },
    sectionCount: {
      fontSize: '0.75rem',
      color: 'var(--gray-500)',
      backgroundColor: 'var(--gray-200)',
      padding: '0.125rem 0.5rem',
      borderRadius: '9999px'
    },
    tasksList: {
      maxHeight: tasksExpanded ? '1000px' : '0',
      overflow: 'hidden',
      transition: 'max-height 0.3s ease-in-out'
    },
    milestonesList: {
      maxHeight: milestonesExpanded ? '500px' : '0',
      overflow: 'hidden',
      transition: 'max-height 0.3s ease-in-out'
    },
    milestone: {
      padding: '0.75rem',
      backgroundColor: 'var(--gray-50)',
      borderRadius: '0.5rem',
      marginBottom: '0.5rem',
      borderLeft: '3px solid var(--primary-400)'
    },
    milestoneTitle: {
      fontSize: '0.875rem',
      fontWeight: '600',
      color: 'var(--gray-800)',
      marginBottom: '0.25rem'
    },
    milestoneDescription: {
      fontSize: '0.8125rem',
      color: 'var(--gray-600)',
      marginBottom: '0.5rem'
    },
    milestoneMeta: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      fontSize: '0.75rem',
      color: 'var(--gray-500)'
    },
    deliverables: {
      marginTop: '0.5rem',
      paddingLeft: '1rem'
    },
    deliverable: {
      fontSize: '0.75rem',
      color: 'var(--gray-600)',
      marginBottom: '0.25rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.375rem'
    },
    timeline: {
      padding: '0.75rem',
      backgroundColor: 'var(--gray-50)',
      borderRadius: '0.5rem',
      fontSize: '0.875rem',
      color: 'var(--gray-700)',
      lineHeight: '1.5'
    },
    resources: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '0.5rem'
    },
    resourceTag: {
      padding: '0.25rem 0.625rem',
      fontSize: '0.75rem',
      backgroundColor: 'var(--purple-50)',
      color: 'var(--purple-700)',
      borderRadius: '0.375rem'
    },
    actions: {
      display: 'flex',
      gap: '0.5rem',
      justifyContent: 'flex-end',
      padding: '1rem',
      borderTop: '1px solid var(--gray-200)',
      backgroundColor: 'var(--gray-50)'
    },
    actionButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.375rem',
      padding: '0.5rem 1rem',
      fontSize: '0.8125rem',
      fontWeight: '500',
      borderRadius: '0.5rem',
      cursor: 'pointer',
      transition: 'all 0.2s'
    },
    primaryButton: {
      backgroundColor: 'var(--primary-600)',
      color: 'white',
      border: 'none'
    },
    secondaryButton: {
      backgroundColor: 'white',
      color: 'var(--gray-700)',
      border: '1px solid var(--gray-300)'
    }
  };

  return (
    <div style={styles.card}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerTop}>
          <div style={styles.headerInfo}>
            <h3 style={styles.title}>
              <ClipboardDocumentListIcon style={{ width: '1.25rem', height: '1.25rem', color: 'var(--primary-600)' }} />
              {plan.title}
            </h3>
            {plan.objective && (
              <p style={styles.objective}>{plan.objective}</p>
            )}
            <div style={styles.stats}>
              <span style={styles.stat}>
                <ClipboardDocumentListIcon style={styles.statIcon} />
                {taskCount} tasks
              </span>
              <span style={styles.stat}>
                <ClockIcon style={styles.statIcon} />
                {totalHours}h total
              </span>
              {milestoneCount > 0 && (
                <span style={styles.stat}>
                  <FlagIcon style={styles.statIcon} />
                  {milestoneCount} milestones
                </span>
              )}
            </div>
          </div>
          <button
            style={styles.expandButton}
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? (
              <ChevronUpIcon style={{ width: '1.25rem', height: '1.25rem' }} />
            ) : (
              <ChevronDownIcon style={{ width: '1.25rem', height: '1.25rem' }} />
            )}
          </button>
        </div>
      </div>

      {/* Body */}
      <div style={styles.body}>
        {/* Timeline */}
        {plan.timeline && (
          <div style={styles.section}>
            <h4 style={{ ...styles.sectionTitle, marginBottom: '0.5rem' }}>
              <CalendarDaysIcon style={styles.sectionIcon} />
              Timeline
            </h4>
            <div style={styles.timeline}>{plan.timeline}</div>
          </div>
        )}

        {/* Tasks Section */}
        {plan.tasks && plan.tasks.length > 0 && (
          <div style={styles.section}>
            <div
              style={styles.sectionHeader}
              onClick={() => setTasksExpanded(!tasksExpanded)}
            >
              <div style={styles.sectionTitle}>
                <ClipboardDocumentListIcon style={styles.sectionIcon} />
                Tasks
                <span style={styles.sectionCount}>{taskCount}</span>
              </div>
              {tasksExpanded ? (
                <ChevronUpIcon style={{ width: '1rem', height: '1rem', color: 'var(--gray-500)' }} />
              ) : (
                <ChevronDownIcon style={{ width: '1rem', height: '1rem', color: 'var(--gray-500)' }} />
              )}
            </div>
            <div style={styles.tasksList}>
              {plan.tasks.map((task, index) => (
                <TaskPreviewCard
                  key={index}
                  task={task}
                  compact={true}
                  onSave={onTaskSave}
                  onEdit={onTaskEdit}
                  onDelete={onTaskDelete}
                />
              ))}
            </div>
          </div>
        )}

        {/* Milestones Section */}
        {plan.milestones && plan.milestones.length > 0 && (
          <div style={styles.section}>
            <div
              style={styles.sectionHeader}
              onClick={() => setMilestonesExpanded(!milestonesExpanded)}
            >
              <div style={styles.sectionTitle}>
                <FlagIcon style={styles.sectionIcon} />
                Milestones
                <span style={styles.sectionCount}>{milestoneCount}</span>
              </div>
              {milestonesExpanded ? (
                <ChevronUpIcon style={{ width: '1rem', height: '1rem', color: 'var(--gray-500)' }} />
              ) : (
                <ChevronDownIcon style={{ width: '1rem', height: '1rem', color: 'var(--gray-500)' }} />
              )}
            </div>
            <div style={styles.milestonesList}>
              {plan.milestones.map((milestone, index) => (
                <div key={index} style={styles.milestone}>
                  <div style={styles.milestoneTitle}>{milestone.title}</div>
                  {milestone.description && (
                    <div style={styles.milestoneDescription}>{milestone.description}</div>
                  )}
                  <div style={styles.milestoneMeta}>
                    <CalendarDaysIcon style={{ width: '0.875rem', height: '0.875rem' }} />
                    {milestone.target_date_offset_days} days from start
                  </div>
                  {milestone.deliverables && milestone.deliverables.length > 0 && (
                    <div style={styles.deliverables}>
                      {milestone.deliverables.map((d, i) => (
                        <div key={i} style={styles.deliverable}>
                          <BookmarkIcon style={{ width: '0.75rem', height: '0.75rem' }} />
                          {d}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Resources */}
        {plan.resources_needed && plan.resources_needed.length > 0 && (
          <div style={styles.section}>
            <h4 style={{ ...styles.sectionTitle, marginBottom: '0.5rem' }}>
              Resources Needed
            </h4>
            <div style={styles.resources}>
              {plan.resources_needed.map((resource, index) => (
                <span key={index} style={styles.resourceTag}>{resource}</span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div style={styles.actions}>
        {onExportJira && (
          <button
            style={{ ...styles.actionButton, ...styles.secondaryButton }}
            onClick={() => onExportJira(plan)}
          >
            <ArrowDownTrayIcon style={{ width: '1rem', height: '1rem' }} />
            Export to Jira
          </button>
        )}
        {onExportAsana && (
          <button
            style={{ ...styles.actionButton, ...styles.secondaryButton }}
            onClick={() => onExportAsana(plan)}
          >
            <ArrowDownTrayIcon style={{ width: '1rem', height: '1rem' }} />
            Export to Asana
          </button>
        )}
        {onSave && (
          <button
            style={{ ...styles.actionButton, ...styles.primaryButton }}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              'Saving...'
            ) : (
              <>
                <PlusIcon style={{ width: '1rem', height: '1rem' }} />
                Save Plan
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default PlanPreviewCard;
